import { fireEvent, render, screen, waitFor } from '@testing-library/react'
import { wrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import type { ReactNode } from 'react'
import { ThemeProvider } from 'styled-components'
import { GeckoTheme } from 'styles/GeckoTheme'
import { beforeAll, beforeEach, describe, expect, it, vi } from 'vitest'

const ticketAccessMock = vi.fn()

vi.mock('features/libra/comments/CommentIndicator', () => ({
  CommentIndicator: ({ children }: { children: ReactNode }) => children,
}))

vi.mock('features/libra/ticket/TicketAccessContext', () => ({
  useTicketAccess: () => ticketAccessMock(),
}))

vi.mock('features/libra/ticket/TicketStateContext', () => ({
  useActiveStepId: () => 'closeout',
}))

vi.mock('features/libra/useRegisterSaveGuard', () => ({
  useRegisterSaveGuard: () => ({
    open: false,
    isSaving: false,
    onSaveAndContinue: vi.fn(),
    onDiscardAndContinue: vi.fn(),
    onCancel: vi.fn(),
  }),
}))

vi.mock('features/permissions', () => ({
  PermissionsGate: ({ children }: { children: ReactNode }) => children,
}))

vi.mock('./GongAgentAutofill', () => ({
  AiAutofillBanner: () => null,
}))

vi.mock('./niiReport/NiiRecommendationsAiPanel', () => ({
  NiiRecommendationsAiPanel: () => null,
}))

vi.mock('./niiReport/cards/NiiBasicInformationCard', () => ({
  NiiBasicInformationCard: () => <div>Basic Information</div>,
}))

vi.mock('./niiReport/cards/NiiInspectionEconomicsCard', () => ({
  NiiInspectionEconomicsCard: () => <div>Inspection economics</div>,
}))

vi.mock('./niiReport/cards/NiiReportInfoCard', () => ({
  NiiReportInfoCard: ({ title }: { title: string }) => <div>{title}</div>,
}))

vi.mock(
  'features/libra/stepComponents/niiReport/pdf/NiiPdfExportButton',
  () => ({
    NiiPdfExportButton: () => <button>Download report</button>,
  })
)

import { Component } from './NiiReport'

const fields = {
  analysisDate: 'analysis_date',
  executiveSummary: 'executive_summary',
  internalComments: 'internal_comments',
  eligibility: 'eligibility',
  eligibilityComputed: 'eligibility_computed',
  justification: 'justification',
  recommendations: 'recommendations',
  inspectableArea: 'inspectable_area',
}

const makeTicketAccess = (
  eligibility: EligibilityStatus,
  computed: boolean = eligibility === ELIGIBILITY_STATUS.ELIGIBLE,
  justification?: string
) => ({
  fieldValues: {
    pre_screening_result: wrapFieldValue(false),
    analysis_date: wrapFieldValue('2026-08-26'),
    executive_summary: wrapFieldValue(''),
    internal_comments: wrapFieldValue(''),
    eligibility: wrapFieldValue(eligibility),
    eligibility_computed: wrapFieldValue(computed),
    ...(justification !== undefined
      ? { justification: wrapFieldValue(justification) }
      : {}),
    recommendations: wrapFieldValue([]),
    inspectable_area: wrapFieldValue(0),
  },
  setFieldValues: vi.fn().mockResolvedValue(undefined),
  isLoading: false,
})

describe('NiiReport', () => {
  beforeAll(() => {
    vi.stubGlobal(
      'ResizeObserver',
      class {
        observe() {}
        unobserve() {}
        disconnect() {}
      }
    )
    Object.defineProperty(window, 'matchMedia', {
      writable: true,
      value: vi.fn().mockImplementation((query: string) => ({
        matches: false,
        media: query,
        onchange: null,
        addListener: vi.fn(),
        removeListener: vi.fn(),
        addEventListener: vi.fn(),
        removeEventListener: vi.fn(),
        dispatchEvent: vi.fn().mockReturnValue(false),
      })),
    })
  })

  beforeEach(() => {
    ticketAccessMock.mockReturnValue(
      makeTicketAccess(ELIGIBILITY_STATUS.NOT_ELIGIBLE)
    )
  })

  it('does not require a closeout recommendation when final eligibility is Not Eligible', () => {
    const setValidationResult = vi.fn()
    render(
      <ThemeProvider theme={GeckoTheme}>
        <Component
          name='NII Closeout'
          setValidationResult={setValidationResult}
          config={{
            fields,
            zoneFieldName: 'zones',
            preScreening: {
              stepId: 'pre-screening',
              resultField: 'pre_screening_result',
            },
          }}
        />
      </ThemeProvider>
    )

    expect(screen.getByText('Basic Information')).toBeTruthy()
    expect(screen.getByText('Executive Summary')).toBeTruthy()
    expect(
      screen.getByText('Internal Assessment Comments and Notes')
    ).toBeTruthy()
    expect(screen.getByText('Conclusion')).toBeTruthy()
    expect(screen.getByText('Recommendations')).toBeTruthy()
    expect(screen.queryByText('Justification')).toBeNull()
    expect(screen.getByText('Inspection economics')).toBeTruthy()
    expect(screen.queryByText('This asset is not eligible for NII')).toBeNull()
    expect(screen.getByText('Not Eligible')).toBeTruthy()
    expect(screen.queryByRole('combobox')).toBeNull()
    expect(screen.getByText('No Changes')).toBeTruthy()
    expect(
      screen.queryByText(
        'Add at least one recommendation or justification before saving or continuing.'
      )
    ).toBeNull()
    expect(setValidationResult).not.toHaveBeenCalled()

    fireEvent.click(screen.getByRole('button', { name: 'Add item' }))

    expect(screen.getByText('Save Changes?')).toBeTruthy()
    expect(
      screen.getByRole<HTMLButtonElement>('button', { name: 'Save' }).disabled
    ).toBe(false)
  })

  it('attributes a Not Eligible result to pre-screening instead of screening justification', () => {
    ticketAccessMock.mockReturnValue(
      makeTicketAccess(
        ELIGIBILITY_STATUS.NOT_ELIGIBLE,
        false,
        'Screening override text.'
      )
    )
    render(
      <ThemeProvider theme={GeckoTheme}>
        <Component
          name='NII Closeout'
          setValidationResult={vi.fn()}
          config={{
            fields,
            zoneFieldName: 'zones',
            preScreening: {
              stepId: 'pre-screening',
              resultField: 'pre_screening_result',
            },
          }}
        />
      </ThemeProvider>
    )

    expect(screen.getByText('Screened out at pre-screening')).toBeTruthy()
    expect(screen.queryByText('Screening override text.')).toBeNull()
  })

  it('displays stored eligibility on Closeout without writing it on save', async () => {
    const access = makeTicketAccess(
      ELIGIBILITY_STATUS.NOT_ELIGIBLE,
      true,
      'Engineer overrode the system screening result.'
    )
    ticketAccessMock.mockReturnValue(access)
    const setValidationResult = vi.fn()
    render(
      <ThemeProvider theme={GeckoTheme}>
        <Component
          name='NII Closeout'
          setValidationResult={setValidationResult}
          config={{
            fields,
            zoneFieldName: 'zones',
          }}
        />
      </ThemeProvider>
    )

    expect(screen.getByText('Not Eligible')).toBeTruthy()
    expect(screen.queryByRole('combobox')).toBeNull()
    expect(screen.getByText('Justification')).toBeTruthy()
    expect(
      screen.getByText('Engineer overrode the system screening result.')
    ).toBeTruthy()
    expect(screen.getByText('Recommendations')).toBeTruthy()
    expect(setValidationResult).not.toHaveBeenCalled()

    fireEvent.click(screen.getByRole('button', { name: 'Add item' }))
    fireEvent.change(screen.getByRole('textbox'), {
      target: { value: 'Screening override stands at closeout.' },
    })
    fireEvent.click(screen.getByRole('button', { name: 'Save' }))

    await waitFor(() => {
      expect(access.setFieldValues).toHaveBeenCalled()
    })
    expect(access.setFieldValues).toHaveBeenCalled()
    expect(access.setFieldValues.mock.calls[0][0].eligibility).toBeUndefined()
    expect(
      access.setFieldValues.mock.calls[0][0].eligibility_computed
    ).toBeUndefined()
    expect(access.setFieldValues.mock.calls[0][0].justification).toBeUndefined()
  })

  it('does not invent eligibility on Closeout save when the ticket field is empty', async () => {
    const access = {
      fieldValues: {
        analysis_date: wrapFieldValue('2026-08-26'),
        executive_summary: wrapFieldValue(''),
        internal_comments: wrapFieldValue(''),
        eligibility_computed: wrapFieldValue(true),
        recommendations: wrapFieldValue([]),
        inspectable_area: wrapFieldValue(0),
      },
      setFieldValues: vi.fn().mockResolvedValue(undefined),
      isLoading: false,
    }
    ticketAccessMock.mockReturnValue(access)
    render(
      <ThemeProvider theme={GeckoTheme}>
        <Component
          name='NII Closeout'
          setValidationResult={vi.fn()}
          config={{
            fields,
            zoneFieldName: 'zones',
          }}
        />
      </ThemeProvider>
    )

    fireEvent.click(screen.getByRole('button', { name: 'Add item' }))
    fireEvent.change(screen.getByRole('textbox'), {
      target: { value: 'Closeout note only.' },
    })
    fireEvent.click(screen.getByRole('button', { name: 'Save' }))

    await waitFor(() => {
      expect(access.setFieldValues).toHaveBeenCalled()
    })
    expect(access.setFieldValues.mock.calls[0][0].eligibility).toBeUndefined()
  })
})
